import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Home, Calendar, Camera, CreditCard, User as UserIcon, Settings, ArrowLeftRight, Thermometer, BookOpen, ExternalLink, Users, Image, KeyRound } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useActiveCompetition } from '../../hooks/useActiveCompetition'
import { useTeamFeature } from '../../hooks/useTeamFeature'
import LogoutButton from '../auth/LogoutButton'
import albatrossIcon from '../../assets/albatross-icon.svg'
import { config } from '../../config/environment'

export function MobileNav() {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const { user } = useAuth()
  const { albatrossCompId } = useActiveCompetition()
  const { enabled: teamEnabled } = useTeamFeature()

  const isAdmin = user?.role === 'admin' || user?.role === 'photo_admin'

  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Events', href: '/events', icon: Calendar },
    { name: 'Photos', href: '/photos', icon: Camera },
    { name: 'Pilot ID Card', href: '/membership', icon: CreditCard },
  ]

  if (teamEnabled) {
    mainNavigation.push({ name: 'Team', href: '/team', icon: Users })
  }

  if (isAdmin) {
    mainNavigation.push({ name: 'Admin', href: '/admin', icon: Settings })
  }

  const albatrossBaseUrl = config.albatrossUrl.replace(/\/api\/?$/, '')
  const fullAnalysisUrl = albatrossCompId
    ? `${albatrossBaseUrl}/competitions/${albatrossCompId}`
    : albatrossBaseUrl

  const isActive = (href: string) => location.pathname === href

  return (
    <>
      {/* Mobile menu button */}
      <button
        type="button"
        className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-fwo-blue hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-fwo-blue"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="sr-only">Open main menu</span>
        {isOpen ? (
          <X className="block h-6 w-6" aria-hidden="true" />
        ) : (
          <Menu className="block h-6 w-6" aria-hidden="true" />
        )}
      </button>

      {/* Mobile menu overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile menu panel */}
      <div
        className={`
          fixed top-0 right-0 bottom-0 w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <UserIcon className="h-5 w-5 text-gray-600" />
              <div className="text-sm">
                <p className="font-medium text-gray-900">{user?.firstName} {user?.lastName}</p>
                <p className="text-gray-500 text-xs">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 px-2 py-4 overflow-y-auto">
            <div className="space-y-1 mb-4">
              {mainNavigation.map((item) => {
                const Icon = item.icon
                const active = isActive(item.href)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`
                      flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors
                      ${active
                        ? 'bg-blue-50 text-fwo-blue'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-fwo-blue'
                      }
                    `}
                  >
                    <Icon className={`mr-3 h-5 w-5 ${active ? 'text-fwo-blue' : 'text-gray-400'}`} />
                    {item.name}
                  </Link>
                )
              })}
              {config.galleryUrl && (
                <a
                  href={config.galleryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:bg-gray-50 hover:text-fwo-blue"
                >
                  <Image className="mr-3 h-5 w-5 text-gray-400" />
                  Gallery
                  <ExternalLink className="ml-auto h-4 w-4 text-gray-400" />
                </a>
              )}
            </div>

            {/* Albatross Analysis section */}
            {albatrossCompId && (
              <div className="border-t border-gray-100 pt-3">
                <p className="px-3 mb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <img src={albatrossIcon} alt="" className="h-3.5 w-3.5" />
                  Albatross Analysis
                </p>
                <a
                  href={fullAnalysisUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:bg-gray-50 hover:text-fwo-blue"
                >
                  <ExternalLink className="mr-3 h-5 w-5 text-gray-400" />
                  Full Analysis
                </a>
                <Link
                  to="/coaching-notes"
                  onClick={() => setIsOpen(false)}
                  className={`
                    flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors
                    ${isActive('/coaching-notes')
                      ? 'bg-blue-50 text-fwo-blue'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-fwo-blue'
                    }
                  `}
                >
                  <BookOpen className={`mr-3 h-5 w-5 ${isActive('/coaching-notes') ? 'text-fwo-blue' : 'text-gray-400'}`} />
                  Coaching Notes
                </Link>
                <Link
                  to="/head-to-head"
                  onClick={() => setIsOpen(false)}
                  className={`
                    flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors
                    ${isActive('/head-to-head')
                      ? 'bg-blue-50 text-fwo-blue'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-fwo-blue'
                    }
                  `}
                >
                  <ArrowLeftRight className={`mr-3 h-5 w-5 ${isActive('/head-to-head') ? 'text-fwo-blue' : 'text-gray-400'}`} />
                  Head-to-Head
                </Link>
                <Link
                  to="/thermal-map"
                  onClick={() => setIsOpen(false)}
                  className={`
                    flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors
                    ${isActive('/thermal-map')
                      ? 'bg-blue-50 text-fwo-blue'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-fwo-blue'
                    }
                  `}
                >
                  <Thermometer className={`mr-3 h-5 w-5 ${isActive('/thermal-map') ? 'text-fwo-blue' : 'text-gray-400'}`} />
                  Thermal Map
                </Link>
              </div>
            )}
          </nav>

          {/* Footer with Change Password & Logout */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <Link
              to="/change-password"
              onClick={() => setIsOpen(false)}
              className="flex items-center px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-fwo-blue transition-colors w-full"
            >
              <KeyRound className="mr-3 h-5 w-5 text-gray-400" />
              Change Password
            </Link>
            <LogoutButton className="w-full" />
          </div>
        </div>
      </div>
    </>
  )
}
